import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  InputRef,
  Space,
  Table,
  TableColumnType,
  Tag,
  Tooltip,
  notification
} from "antd"; // Import Table from antd
import { SubmitHandler, useForm } from "react-hook-form";
import {
  CloseOutlined,
  CommentOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";

//utils
import { BASE_URL } from "../../../utils/constants";
import { User, UserIndex } from "utils/interfaces";
import { FilterDropdownProps, TablePaginationConfig } from "antd/es/table/interface";
import Search from "antd/es/input/Search";
import CommentsComponent from "../../../Components/CommentsComponent";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";
import AddUserModal from "../../../Components/UsersPageComp/Modals/AddUserModal";
import EditUserModal from "../../../Components/UsersPageComp/Modals/EditUserModal";
import ShowApiKeyModal from "../../../Components/UsersPageComp/Modals/ShowApiKeyModal";
import ManageActionsModal from "../../../Components/UsersPageComp/Modals/ManageActionsModal";
import ManageEndpointsModal from "../../../Components/UsersPageComp/Modals/ManageEndpointsModal";
import styles from '../../../global.module.css'

axios.defaults.withCredentials = true;

const UsersView: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showManageActionsModal, setShowManageActionsModal] = useState(false);
  const [manageActionsUser, setManageActionsUser] = useState<User | null>(null);
  const [showManageEndpointsModal, setShowManageEndpointsModal] = useState(false);
  const [manageEndpointsUser, setManageEndpointsUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  // Tracks whether any column filter is currently applied - antd's Table
  // reports this via onChange regardless of which mechanism (filterDropdown
  // search box or a plain `filters` list) drove it. Drives the filter-chips
  // row in the card title (see filterChips below).
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const handleTableChange = (_pagination: TablePaginationConfig, filters: Record<string, any>) => {
    setActiveFilters(filters);
  };

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(0);

  const [api, contextHolder] = notification.useNotification();

  // antd's virtual Table needs a concrete pixel height (rc-virtual-list uses
  // it to compute how many rows to render) - a CSS `calc()` string works for
  // a plain scroll container but not here, so track the equivalent number.
  const [tableScrollY, setTableScrollY] = useState(() => window.innerHeight - 255);
  useEffect(() => {
    const onResize = () => setTableScrollY(window.innerHeight - 255);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const {
    control: userAddControl,
    handleSubmit: userAddHandleSubmit,
    formState: { errors: userAddErrors, isValid: userAddIsValid },
    reset: userAddReset,
  } = useForm<User>({mode: "onChange"});

    const {
    control: userEditControl,
    handleSubmit: userEditHandleSubmit,
    formState: { errors: userEditErrors, isValid: userEditIsValid, isDirty: userEditIsDirty },
    reset: userEditReset,
  } = useForm<User>({mode: "onChange"});

  // Shared across add/edit/delete on this page since only one of those
  // modals is ever open at once - disables the relevant OK/Delete button
  // and shows a spinner while its request is in flight.
  const [loadingButtons, setLoadingButtons] = useState(false);

  const updateUsers = () => {
    axios
      .get(`${BASE_URL}get_users`)
      .then((response) => {
        setUsers(response.data);
        console.log("Users fetched successfully:", response.data);
      })
      .catch((error) => {
        console.error("Error fetching Users:", error);
      });
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: UserIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters: () => void, close: () => void) => {
    clearFilters();
    setSearchText("");

    close();
  };

  // One color per filterable column - used both for that column's chips in
  // the title bar and for the little dot next to the column's own header,
  // so a chip and the header it came from are visually traceable at a
  // glance. Same scheme as the other list pages' filter bars.
  const COLUMN_FILTER_COLORS: Record<string, string> = {
    user_email: "#1677ff",
    is_service_account: "#722ed1",
    is_admin: "#faad14",
  };

  const COLUMN_FILTER_LABELS: Record<string, string> = {
    user_email: "Email / Identifier",
    is_service_account: "Type",
    is_admin: "Role",
  };

  // Small colored dot appended to a column's title while it has an active
  // filter - same color as that column's chips in the title bar.
  const renderColumnTitle = (key: string, label: string) => (
    <Space size={6}>
      <span>{label}</span>
      {Array.isArray(activeFilters[key]) && activeFilters[key].length > 0 && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            display: "inline-block",
            background: COLUMN_FILTER_COLORS[key],
          }}
        />
      )}
    </Space>
  );

  // Drops one value from one column's filter, leaving any other selected
  // values on that same column untouched.
  const removeFilterValue = (key: string, value: any) => {
    setActiveFilters((prev) => {
      const current = prev[key];
      if (!Array.isArray(current)) return prev;
      const next = current.filter((v) => v !== value);
      const updated = { ...prev };
      if (next.length > 0) {
        updated[key] = next;
      } else {
        delete updated[key];
      }
      return updated;
    });
    if (searchedColumn === key) {
      setSearchText("");
      setSearchedColumn("");
    }
  };

  // Every currently applied column filter, as one flat list of removable
  // chips - shown in the card title (see the filter-chips row below).
  const filterChips = useMemo(() => {
    const chips: { key: string; label: string; color: string; onRemove: () => void }[] = [];
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => {
        const displayValue =
          key === "is_service_account"
            ? (value ? "API Key Only" : "Requires Login")
            : key === "is_admin"
            ? (value ? "Admin" : "User")
            : value;
        chips.push({
          key: `${key}-${value}`,
          label: `${COLUMN_FILTER_LABELS[key] ?? key}: ${displayValue}`,
          color: COLUMN_FILTER_COLORS[key] ?? "default",
          onRemove: () => removeFilterValue(key, value),
        });
      });
    });
    return chips;
  }, [activeFilters, searchedColumn]);// eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setActiveFilters({});
    setSearchText("");
    setSearchedColumn("");
  };

  const onSubmitAddUser: SubmitHandler<User> = (data) => {
    console.log(data);
    setLoadingButtons(true);
    axios
      .post(`${BASE_URL}add_user`, {
        user_email: data.user_email,
      })
      .then((response) => {
        console.log(response.data);
        updateUsers();
        toggleAddUserModal();
        api.success({
          title: 'Service account added successfully',
          description: `Service account "${data.user_email}" added successfully.`,
          placement: 'bottomRight',
          showProgress: true
        });
        // Every account created here is a service account and gets its API
        // key generated on creation - show it right away since this is the
        // only time it's returned in full.
        if (response.data.api_key) {
          setApiKey(response.data.api_key);
          setShowApiKeyModal(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching Users:", error);
        api.error({
          title: 'Error adding service account',
          description: `Failed to add service account "${data.user_email}".`,
          placement: 'bottomRight',
          showProgress: true
        });
      })
      .finally(() => setLoadingButtons(false));
  };

  const onSubmitEditUser: SubmitHandler<User> = (data) => {
    console.log("Editing User", data);
    setLoadingButtons(true);
    axios
      .post(`${BASE_URL}update_user/${data?.id}`, {
        user_email: data.user_email,
      })
      .then((response) => {
        updateUsers();
        toggleEditUserModal();
        api.success({
          title: 'User updated successfully',
          description: `User ${data.user_email} updated successfully.`,
          placement: 'bottomRight',
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error fetching Users:", error);
        api.error({
          title: 'Error updating user',
          description: `Failed to update user ${data.user_email}.`,
          placement: 'bottomRight',
          showProgress: true
        });
      })
      .finally(() => setLoadingButtons(false));
  };

  const toggleEditUserModal = () => {
    setShowEditUserModal(!showEditUserModal);
  };
  const toggleAddUserModal = () => {
    userAddReset();
    setShowAddUserModal(!showAddUserModal);
  };

  const toggleApiModal = () => {
    setShowApiKeyModal(!showApiKeyModal);
  };
  const openCommentModal = (user: User) => {
    setSelectedUser(user);
    setSelectedUserId(user.id);
    setShowCommentModal(true);
  };
  const closeCommentModal = () => {
    setShowCommentModal(false);
  };

  useEffect(() => {
    setLoading(true);
    // Fetch the users from the api
    updateUsers();
    setLoading(false);

  }, []);
  // @ts-ignore
  // @ts-ignore
  // @ts-ignore
  const getColumnSearchProps = (
    dataIndex: UserIndex
  ): TableColumnType<User> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Search
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onSearch={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "flex" }}
          allowClear={{
            clearIcon: (
              <CloseOutlined
                onClick={() => clearFilters && handleReset(clearFilters, close)}
              />
            ),
          }}
        />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <>
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      </>
    ),
    // Controlled so the filter-chips row (see filterChips) can clear this
    // column's search from outside the column's own dropdown.
    filteredValue: activeFilters[dataIndex as string] ?? null,
    onFilter: (value, record) => {
      // @ts-ignore
      const fieldValue = record[dataIndex];
      return fieldValue != null && fieldValue
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  // Columns definition for Ant Design Table
  const columns = [
    {
      title: renderColumnTitle("user_email", "Email / Identifier"),
      dataIndex: "user_email",
      key: "user_email",
      width: 280,
      showSorterTooltip: { target: "full-header" },
      sorter: (a: User, b: User) => a.user_email.localeCompare(b.user_email),
      ...getColumnSearchProps("user_email"),
    },
    {
      title: renderColumnTitle("is_service_account", "Type"),
      dataIndex: "is_service_account",
      key: "is_service_account",
      width: 160,
      filters: [
        { text: "Requires Login", value: false },
        { text: "API Key Only", value: true },
      ],
      filteredValue: activeFilters.is_service_account ?? null,
      onFilter: (value: boolean | React.Key, record: User) => record.is_service_account === value,
      render: (isServiceAccount: boolean) =>
        isServiceAccount
          ? <Tag color="purple">API Key Only</Tag>
          : <Tag color="blue">Requires Login</Tag>,
    },
    {
      title: renderColumnTitle("is_admin", "Role"),
      dataIndex: "is_admin",
      key: "is_admin",
      width: 120,
      filters: [
        { text: "Admin", value: true },
        { text: "User", value: false },
      ],
      filteredValue: activeFilters.is_admin ?? null,
      onFilter: (value: boolean | React.Key, record: User) =>
        !record.is_service_account && record.is_admin === value,
      render: (isAdmin: boolean, record: User) =>
        record.is_service_account
          ? "-"
          : isAdmin
          ? <Tag color="gold">Admin</Tag>
          : <Tag>User</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      fixed: "right" as const,
      render: (_text: any, record: User) => (
        <Space wrap>
          <Tooltip title={record.is_service_account ? "" : "This user signs in with Azure - their email can't be changed here."}>
            <Button onClick={() => handleEdit(record)} disabled={!record.is_service_account}>Edit</Button>
          </Tooltip>

          {record.is_service_account ? (
            <Button onClick={() => handleManageEndpoints(record)}>
              Manage Endpoints
            </Button>
          ) : (
            <Tooltip title={record.is_admin ? "Admins have access everywhere - actions can't be restricted." : ""}>
              <Button onClick={() => handleManageActions(record)} disabled={record.is_admin}>
                Manage Actions
              </Button>
            </Tooltip>
          )}

          <Button onClick={() => handleApiKey(record.id)}>
            API Key
          </Button>
          <Button danger onClick={() => handleDeleteRole(record)}>
            Delete
          </Button>
          <Tooltip title="View Comments">
            <Button
              icon={<CommentOutlined />}
              onClick={() => openCommentModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleEdit = (user: User) => {
    // Set the values of the selected user in the form fields
    userEditReset(user);
    setSelectedUser(user);
    toggleEditUserModal();
  };

  const handleManageActions = (user: User) => {
    setManageActionsUser(user);
    setShowManageActionsModal(true);
  };

  const closeManageActionsModal = () => {
    setShowManageActionsModal(false);
    setManageActionsUser(null);
  };

  const handleManageEndpoints = (user: User) => {
    setManageEndpointsUser(user);
    setShowManageEndpointsModal(true);
  };

  const closeManageEndpointsModal = () => {
    setShowManageEndpointsModal(false);
    setManageEndpointsUser(null);
    updateUsers();
  };

  const handleDeleteRole = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteRole = (userId: number, userEmail: string) => {
    console.log("Delete User with ID:", userId);
    axios
      .post(`${BASE_URL}delete_user/${userId}`)
      .then((response) => {
        updateUsers();
        api.success({
          title: 'User deleted successfully',
          description: `User ${userEmail} deleted successfully.`,
          placement: 'bottomRight',
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error fetching Users:", error);
        api.error({
          title: 'Error deleting user',
          description: `Failed to delete user ${userEmail}.`,
          placement: 'bottomRight',
          showProgress: true
        });
      });

    setSelectedUser(null);
    setShowDeleteModal(false);
  };

  const handleApiKey = (userId: number) => {
    console.log("Setting up the api key", userId);
    axios
      .post(`${BASE_URL}generate_api_key/${userId}`)
      .then((response) => {
        console.log(response);
        console.log(response.data);
        const api_key = response.data.content;
        setApiKey(api_key);
        toggleApiModal();
      })
      .catch((error) => {
        const response = error.response.data;
        const api_key = response.reason;
        setApiKey(api_key);
        toggleApiModal();
        console.error("Error fetching Users:", error);
      });
  };

  return (
    <div>
      {contextHolder}
      <div>
        <Card
          className={styles.globalCard}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span style={{ flexShrink: 0 }}>Users</span>
              {filterChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, padding: "2px 0" }}>
                    {filterChips.map((chip) => (
                      <Tag
                        key={chip.key}
                        color={chip.color}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          chip.onRemove();
                        }}
                        style={{ flexShrink: 0 }}
                      >
                        {chip.label}
                      </Tag>
                    ))}
                  </div>
                  <Button size="small" type="link" onClick={clearAllFilters} style={{ flexShrink: 0 }}>
                    Clear all
                  </Button>
                </div>
              )}
              <span style={{ flexShrink: 0, marginLeft: filterChips.length > 0 ? 0 : "auto" }}>
                <Button onClick={toggleAddUserModal} style={{fontSize:12}} type="primary">Add Service Account</Button>
              </span>
            </div>
          }
        >
          <Table
            /*@ts-ignore*/
            columns={columns}
            dataSource={users}
            showSorterTooltip={{ target: "sorter-icon" }}
            loading={loading}
            scroll={{x : 1020, y: tableScrollY}}
            virtual
            pagination={false}
            rowKey="id"
            onChange={handleTableChange}
            className='horizontally-synced-table'
          />
        </Card>
      </div>
      <AddUserModal
        showAddUserModal={showAddUserModal}
        toggleAddUserModal={toggleAddUserModal}
        userAddErrors={userAddErrors}
        userAddControl={userAddControl}
        userAddHandleSubmit={userAddHandleSubmit}
        onSubmit={onSubmitAddUser}
        isValid={userAddIsValid}
        isSubmitting={loadingButtons}
      />
      <EditUserModal
        showEditUserModal={showEditUserModal}
        toggleEditUserModal={toggleEditUserModal}
        userEditErrors={userEditErrors}
        userEditControl={userEditControl}
        userEditHandleSubmit={userEditHandleSubmit}
        onSubmit={onSubmitEditUser}
        selectedUser={selectedUser}
        isValid={userEditIsValid}
        isDirty={userEditIsDirty}
        isSubmitting={loadingButtons}
      />
      <ManageActionsModal
        showModal={showManageActionsModal}
        onClose={closeManageActionsModal}
        userId={manageActionsUser?.id ?? null}
        userEmail={manageActionsUser?.user_email ?? ""}
        api={api}
      />
      <ManageEndpointsModal
        showModal={showManageEndpointsModal}
        onClose={closeManageEndpointsModal}
        user={manageEndpointsUser}
        api={api}
      />
      <ShowApiKeyModal
        showApiKeyModal={showApiKeyModal}
        toggleApiModal={toggleApiModal}
        apiKey={apiKey}
      />
      <CommentsComponent
        showModal={showCommentModal}
        onClose={closeCommentModal}
        commentTarget={"USER"}
        targetId={selectedUserId}
        targetObject={selectedUser}
        api={api}
      />
      <DeleteModal
        showModal={showDeleteModal}
        onDelete={() => confirmDeleteRole(selectedUser!.id, selectedUser!.user_email)}
        onClose={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default UsersView;
